import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { apiDelete, apiPatch, API_BASE_URL, apiPost, Admin } from "../lib/api";
import FollowButton from "./FollowButton";
import CommentsPanel from "./CommentsPanel";
import LikeButton from "./LikeButton";
import MoreMenuForUser from "./MoreMenuForUser";
import Avatar from "./Avatar";
import ImageModal from "./ImageModal";
import TranslateButton from "./TranslateButton";
import RichText from "./RichText";
import { UserBadgeList } from "./UserBadge";
import { BoostFrame } from "./BoostBadge";

export type PostAuthor = {
  id?: string;
  username?: string;
  email?: string;
  avatarUrl?: string | null;
  avatarUrlSmall?: string | null;
  roles?: string[];
  role?: "user" | "vip" | "moderator" | "admin";
  isAdmin?: boolean;
  totalStars?: number;
  savwarTP?: number;
  savwarRank?: number;
  
  // Premium fields
  activeFrame?: string | null;
  activeBadge?: string | null;
  activeBackground?: string | null;
};

export type PostItem = {
  id: string;
  text: string;
  createdAt: string;
  author?: PostAuthor | null;
  highlight?: "gold" | null;
  likeCount?: number;
};

export type PreloadedMeta = {
  likeCount?: number;
  liked?: boolean;
  followingAuthor?: boolean;
  blockRelation?: "none" | "i_blocked" | "blocked_me" | "both";
};

function absUrl(url?: string | null): string | undefined {
  if (!url) return undefined;
  return url.startsWith("/") ? `${API_BASE_URL}${url}` : url;
}

const MAX_LEN = 1000;

function isGoldAuthor(a?: PostAuthor | null, highlight?: string | null) {
  if (highlight === "gold") return true;
  if (!a) return false;
  if (a.isAdmin) return true;
  return Array.isArray(a.roles) && (a.roles.includes("admin") || a.roles.includes("moderator"));
}

export default function PostCard({
  item,
  onChanged,
  meta,
}: {
  item: PostItem;
  onChanged?: (next: PostItem | null) => void;
  meta?: PreloadedMeta;
}) {
  const { user: me } = useAuth();
  const navigate = useNavigate();

  // 🔧 ID Normalization: Ensure we have a valid postId
  const postId = item.id || (item as any)._id || "";

  const myId = (me as any)?.id ?? (me as any)?._id ?? null;
  const myRoles: string[] = Array.isArray((me as any)?.roles) ? (me as any).roles : [];
  const myIsAdmin: boolean = Boolean((me as any)?.isAdmin) || myRoles.includes("admin");
  const myIsModerator: boolean = myRoles.includes("moderator");

  const isMine = useMemo(() => {
    if (!me) return false;
    const a = item.author;
    if (!a) return false;
    const byId = a.id && myId && String(a.id) === String(myId);
    const byUsername = a.username && (me as any)?.username && String(a.username) === String((me as any).username);
    return Boolean(byId || byUsername);
  }, [me, myId, item.author]);

  const canEdit = isMine || myIsAdmin;
  const canDelete = isMine || myIsAdmin || myIsModerator;

  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(item.text);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  const [openAvatar, setOpenAvatar] = useState(false);
  const [showComments, setShowComments] = useState(false);

  // 🚀 FAZ 8: Post Boost state
  const [isBoosted, setIsBoosted] = useState(false);
  const [boostBusy, setBoostBusy] = useState(false);

  useEffect(() => {
    if (!isEditing) setText(item.text);
  }, [item.text, isEditing]);

  function startEdit() {
    if (!canEdit) return;
    setErr(null);
    setText(item.text);
    setIsEditing(true);
    setTimeout(() => taRef.current?.focus(), 0);
  }

  async function handleDelete() {
    if (!canDelete || busy) return;
    if (!confirm("Bu paylaşımı silmek istiyor musun?")) return;
    setBusy(true);
    setErr(null);
    try {
      await apiDelete<{ ok: true }>(`/api/posts/${postId}`);
      onChanged?.(null);
    } catch (e: any) {
      setErr(e?.message || "Silme işlemi başarısız oldu.");
    } finally {
      setBusy(false);
    }
  }

  async function handleSave() {
    if (!canEdit || busy) return;
    const trimmed = text.trim();
    if (!trimmed) return setErr("Gönderi boş olamaz.");
    try {
      setBusy(true);
      setErr(null);
      const res = await apiPatch<{ ok: true; item: PostItem }>(`/api/posts/${postId}`, {
        text: trimmed.slice(0, MAX_LEN),
      });
      onChanged?.(res.item);
      setIsEditing(false);
    } catch (e: any) {
      setErr(e?.message || "Kaydetme başarısız oldu.");
    } finally {
      setBusy(false);
    }
  }

  // 🚀 FAZ 8: Boost/Remove Boost handlers
  async function handleBoost() {
    if (!myIsAdmin || boostBusy) return;
    setBoostBusy(true);
    try {
      // Basit boost: multiplier 2, 7 gün süre
      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      await Admin.boostPost(postId, { multiplier: 2.0, expiresAt, reason: "Admin boost" });
      setIsBoosted(true);
      alert("Post boost edildi! (2x çarpan, 7 gün süre)");
    } catch (e: any) {
      alert(e?.message || "Boost başarısız oldu.");
    } finally {
      setBoostBusy(false);
    }
  }

  async function handleRemoveBoost() {
    if (!myIsAdmin || boostBusy) return;
    setBoostBusy(true);
    try {
      await Admin.removeBoost(postId);
      setIsBoosted(false);
      alert("Boost kaldırıldı.");
    } catch (e: any) {
      alert(e?.message || "Boost kaldırma başarısız oldu.");
    } finally {
      setBoostBusy(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Escape") {
      e.stopPropagation();
      setIsEditing(false);
      setText(item.text);
      setErr(null);
      return;
    }
    const key = e.key.toLowerCase();
    if ((e.ctrlKey || e.metaKey) && (key === "enter" || key === "s")) {
      e.preventDefault();
      handleSave();
    }
  }

  const a = item.author;
  const avatarSmall = absUrl(a?.avatarUrlSmall || a?.avatarUrl || null);
  const avatarBig = absUrl(a?.avatarUrl || a?.avatarUrlSmall || null);

  const trimmed = text.trim();
  const changed = trimmed !== item.text.trim();
  const canSave = canEdit && !busy && !!trimmed && trimmed.length <= MAX_LEN && changed;

  const gold = isGoldAuthor(a, item.highlight ?? null);

  // Premium user check (has frame or badge)
  const isPremiumAuthor = Boolean(a?.activeFrame || a?.activeBadge);

  // Boost kontrolü (meta veya item'den likeCount)
  const totalLikes = meta?.likeCount ?? item.likeCount ?? 0;
  const boostLevel: 1000 | 5000 | 10000 | null =
    totalLikes >= 10000 ? 10000 : totalLikes >= 5000 ? 5000 : totalLikes >= 1000 ? 1000 : null;

  const cardContent = (
    <div
      className="post-card"
      style={{
        border: isPremiumAuthor 
          ? "1px solid rgba(250, 204, 21, 0.7)" 
          : gold 
          ? "1px solid rgba(255, 215, 0, 0.7)" 
          : "1px solid #2c2c2c",
        boxShadow: isPremiumAuthor
          ? "0 0 20px rgba(250, 204, 21, 0.25)"
          : gold 
          ? "0 0 0 1px rgba(255,215,0,0.15), 0 0 12px rgba(255,215,0,0.08)" 
          : "none",
        borderRadius: 10,
        padding: 12,
        background: gold
          ? "linear-gradient(180deg, rgba(34,27,0,0.35) 0%, rgba(20,20,20,0.6) 100%)"
          : "transparent",
      }}
    >
      {/* üst satır */}
      <div
        className="post-card-header"
        style={{
          fontSize: 12,
          opacity: 0.9,
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        {/* Avatar + kullanıcı adı + tarih */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: "1 1 auto" }}>
          {/* Avatar with Premium Frame */}
          <div style={{ position: "relative", display: "inline-block" }}>
            <Avatar
              src={avatarSmall}
              alt={a?.username || "avatar"}
              size={32}
              onClick={() => avatarBig && setOpenAvatar(true)}
            />
            {a?.activeFrame && (
              <img
                src={a.activeFrame}
                alt="Premium Frame"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                  objectFit: "contain",
                }}
              />
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, minWidth: 0, display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              {a?.username ? (
                <Link
                  to={`/u/${encodeURIComponent(a.username)}`}
                  style={{ textDecoration: "none", color: gold ? "gold" : "inherit" }}
                >
                  @{a.username}
                </Link>
              ) : (
                "Anonim"
              )}
              {/* Premium Badge */}
              {a?.activeBadge && (
                <img
                  src={a.activeBadge}
                  alt="Premium Badge"
                  style={{
                    width: 16,
                    height: 16,
                    objectFit: "contain",
                  }}
                />
              )}
              {gold && (
                <span
                  title="Admin/Moderatör"
                  style={{
                    fontSize: 10,
                    padding: "2px 6px",
                    borderRadius: 999,
                    border: "1px solid rgba(255,215,0,0.7)",
                    background: "rgba(255,215,0,0.12)",
                    color: "gold",
                    letterSpacing: 0.2,
                  }}
                >
                  GOLD
                </span>
              )}
            </span>
            {/* User Badge'ler (Admin/Mod/VIP) */}
            {a && <UserBadgeList user={a} />}
            <span style={{ opacity: 0.7, flexShrink: 0 }}>•</span>
            <span style={{ whiteSpace: "nowrap", opacity: 0.8, fontSize: 11 }}>
              {new Date(item.createdAt).toLocaleString()}
            </span>
          </div>
        </div>

        <div className="post-card-actions" style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          {/* 🚀 FAZ 8: Admin Post Boost UI */}
          {myIsAdmin && !isEditing && (
            <>
              {!isBoosted ? (
                <button
                  onClick={handleBoost}
                  disabled={boostBusy}
                  title="Bu posta boost ver (2x çarpan, 7 gün)"
                  style={{ 
                    fontSize: 11, 
                    padding: "4px 8px",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    border: "none",
                    color: "white",
                    borderRadius: 6,
                    cursor: boostBusy ? "not-allowed" : "pointer",
                    opacity: boostBusy ? 0.6 : 1,
                  }}
                >
                  {boostBusy ? "⏳" : "🚀 Boost"}
                </button>
              ) : (
                <button
                  onClick={handleRemoveBoost}
                  disabled={boostBusy}
                  title="Boost'u kaldır"
                  style={{ 
                    fontSize: 11, 
                    padding: "4px 8px",
                    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                    border: "none",
                    color: "white",
                    borderRadius: 6,
                    cursor: boostBusy ? "not-allowed" : "pointer",
                    opacity: boostBusy ? 0.6 : 1,
                  }}
                >
                  {boostBusy ? "⏳" : "✖ Boostu Kapat"}
                </button>
              )}
            </>
          )}

          {/* Düzenle/Sil */}
          {!isEditing && (canEdit || canDelete) && (
            <>
              {canEdit && (
                <button onClick={startEdit} disabled={busy} title="Düzenle" style={{ fontSize: 11, padding: "4px 8px" }}>
                  Düzenle
                </button>
              )}
              {canDelete && (
                <button onClick={handleDelete} disabled={busy} title="Sil" style={{ fontSize: 11, padding: "4px 8px" }}>
                  Sil
                </button>
              )}
            </>
          )}

          {/* Başkasının postuysa takip & menü */}
          {!isMine && a?.id && (
            <>
              <FollowButton userId={a.id} username={a?.username} />
              <MoreMenuForUser className="kebab-menu" targetUserId={a.id} />
            </>
          )}

          <LikeButton postId={postId} />
        </div>
      </div>

      {/* gövde */}
      <div style={{ marginTop: 6 }}>
        {isEditing ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <textarea
              ref={taRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={3}
              maxLength={MAX_LEN}
              style={{ width: "100%" }}
              placeholder="Gönderiyi düzenle…"
            />
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <button onClick={handleSave} disabled={!canSave} title="Kaydet (Ctrl/Cmd+Enter)">
                {busy ? "Kaydediliyor…" : "Kaydet"}
              </button>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setText(item.text);
                  setErr(null);
                }}
                disabled={busy}
              >
                İptal
              </button>
              <span style={{ marginLeft: "auto", fontSize: 12, opacity: 0.7 }}>
                {trimmed.length}/{MAX_LEN}
              </span>
            </div>
          </div>
        ) : (
          <div style={{ color: gold ? "gold" : "inherit" }}>
            <RichText text={item.text} />
            {item.text ? <TranslateButton text={item.text} /> : null}
          </div>
        )}
      </div>

      {err && <div style={{ color: "crimson", marginTop: 8 }}>{err}</div>}

      {/* Yorumlar ve Post Detayı */}
      <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <button
          onClick={() => navigate(`/post/${postId}`)}
          title="Gönderi detayına git"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            fontSize: 14,
            fontWeight: 600,
            borderRadius: 6,
            border: "1px solid #666",
            background: "transparent",
            color: "#666",
            cursor: "pointer",
          }}
        >
          <span>💬</span>
          <span>Yorumları Gör</span>
        </button>
        <button
          onClick={() => setShowComments((s) => !s)}
          title="Yorumları inline göster/gizle"
          aria-expanded={showComments}
          style={{
            padding: "6px 12px",
            fontSize: 13,
            borderRadius: 6,
            border: "1px solid #444",
            background: "transparent",
            color: "#999",
            cursor: "pointer",
          }}
        >
          {showComments ? "Inline Gizle" : "Inline Göster"}
        </button>
      </div>

      {showComments && (
        <div style={{ marginTop: 8 }}>
          <CommentsPanel postId={postId} />
        </div>
      )}

      {/* Avatar büyük */}
      <ImageModal
        open={openAvatar}
        src={avatarBig}
        alt={a?.username || "avatar"}
        caption={a?.username ? `@${a.username}` : undefined}
        onClose={() => setOpenAvatar(false)}
      />
    </div>
  );

  // Boost'lu postları BoostFrame ile sarmalayalım
  if (boostLevel) {
    return <BoostFrame boostLevel={boostLevel}>{cardContent}</BoostFrame>;
  }

  return cardContent;
}
